import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Save, RefreshCw, Palette, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export const BrandingManager = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await apiService.getSetting(token, 'branding');
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateSetting(token, 'branding', settings);
      toast.success('Branding settings saved!');
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="branding-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Branding & Appearance</h1>
          <p className="text-slate-500">Customize your website's look and feel</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Logo & Favicon */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Logo & Favicon</CardTitle>
          <CardDescription>Your brand identity assets</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label>Logo URL</Label>
              {settings?.logo_url && (
                <div className="p-4 bg-slate-100 rounded-lg">
                  <img src={settings.logo_url} alt="Logo Preview" className="max-h-20 object-contain" />
                </div>
              )}
              <Input
                value={settings?.logo_url || ''}
                onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                placeholder="https://... (logo image URL)"
              />
              <p className="text-xs text-slate-500">Recommended: PNG or SVG with transparent background</p>
            </div>
            <div className="space-y-3">
              <Label>Favicon URL</Label>
              {settings?.favicon_url && (
                <div className="p-4 bg-slate-100 rounded-lg">
                  <img src={settings.favicon_url} alt="Favicon Preview" className="w-8 h-8 object-contain" />
                </div>
              )}
              <Input
                value={settings?.favicon_url || ''}
                onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })}
                placeholder="https://... (favicon URL)"
              />
              <p className="text-xs text-slate-500">Recommended: 32x32 or 64x64 PNG/ICO</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brand Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> Brand Colors</CardTitle>
          <CardDescription>Your website's color scheme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <Label>Primary Color</Label>
              <div className="flex gap-3">
                <input
                  type="color"
                  value={settings?.primary_color || '#0ea5e9'}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                />
                <Input
                  value={settings?.primary_color || '#0ea5e9'}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  placeholder="#0ea5e9"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">Main accent color (links, buttons)</p>
            </div>
            <div className="space-y-3">
              <Label>Secondary Color</Label>
              <div className="flex gap-3">
                <input
                  type="color"
                  value={settings?.secondary_color || '#f97316'}
                  onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                />
                <Input
                  value={settings?.secondary_color || '#f97316'}
                  onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  placeholder="#f97316"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">Secondary accent (highlights, CTAs)</p>
            </div>
            <div className="space-y-3">
              <Label>Dark Color</Label>
              <div className="flex gap-3">
                <input
                  type="color"
                  value={settings?.dark_color || '#0f172a'}
                  onChange={(e) => setSettings({ ...settings, dark_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                />
                <Input
                  value={settings?.dark_color || '#0f172a'}
                  onChange={(e) => setSettings({ ...settings, dark_color: e.target.value })}
                  placeholder="#0f172a"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">Dark background (footer, headers)</p>
            </div>
          </div>

          {/* Color Preview */}
          <div className="p-6 border rounded-lg">
            <p className="text-sm text-slate-500 mb-4">Preview:</p>
            <div className="flex gap-4 flex-wrap">
              <div 
                className="w-24 h-24 rounded-lg shadow-md flex items-center justify-center text-white text-xs font-medium"
                style={{ backgroundColor: settings?.primary_color || '#0ea5e9' }}
              >
                Primary
              </div>
              <div 
                className="w-24 h-24 rounded-lg shadow-md flex items-center justify-center text-white text-xs font-medium"
                style={{ backgroundColor: settings?.secondary_color || '#f97316' }}
              >
                Secondary
              </div>
              <div 
                className="w-24 h-24 rounded-lg shadow-md flex items-center justify-center text-white text-xs font-medium"
                style={{ backgroundColor: settings?.dark_color || '#0f172a' }}
              >
                Dark
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header & Footer */}
      <Card>
        <CardHeader>
          <CardTitle>Header & Footer</CardTitle>
          <CardDescription>Customize header CTA and footer text</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Header CTA Text</Label>
              <Input
                value={settings?.header_cta_text || ''}
                onChange={(e) => setSettings({ ...settings, header_cta_text: e.target.value })}
                placeholder="Get Quote"
              />
            </div>
            <div className="space-y-2">
              <Label>Header CTA Link</Label>
              <Input
                value={settings?.header_cta_link || ''}
                onChange={(e) => setSettings({ ...settings, header_cta_link: e.target.value })}
                placeholder="/contact"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Footer Text</Label>
            <Input
              value={settings?.footer_text || ''}
              onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
              placeholder="Short description for the footer"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default BrandingManager;
